import React, { useState } from 'react';
import { X, AlertCircle, XCircle } from 'lucide-react';
import { updateCompanyStatus } from '../../../../services/companyService';
import { useAuth } from '../../../../contexts/AuthContext';

const SuspendCompanyModal = ({ company, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!reason?.trim() || !isConfirmed) return;

    setIsSubmitting(true);
    setError('');

    try {
      const newStatus = isActive ? 'suspended' : 'active';
      const result = await updateCompanyStatus(company.id, newStatus, reason, user?.id);

      if (!result.success) {
        throw new Error(result.error);
      }

      // Call onSuccess callback to refresh the company list
      if (onSuccess) {
        await onSuccess();
      }

      // Close modal
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to update company status');
      setIsSubmitting(false);
    }
  };

  const isActive = company?.status === 'active';
  const actionText = isActive ? 'Suspend' : 'Reactivate';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] border border-border rounded-lg max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            {isActive ? (
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-green-400" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-text-primary">{actionText} Company</h2>
              <p className="text-text-secondary text-sm">{company?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Impact Warning */}
          <div className={`${isActive ? 'bg-red-500/10 border-red-500/20' : 'bg-green-500/10 border-green-500/20'} border rounded-lg p-4`}>
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-5 h-5 ${isActive ? 'text-red-400' : 'text-green-400'} flex-shrink-0 mt-0.5`} />
              <div className="flex-1">
                <p className={`${isActive ? 'text-red-400' : 'text-green-400'} font-medium mb-2`}>
                  {isActive ? 'Suspension Impact' : 'Reactivation Impact'}
                </p>
                <ul className="text-text-secondary text-sm space-y-1">
                  {isActive ? (
                    <>
                      <li>• All users will be immediately logged out</li>
                      <li>• No one from this company can login</li>
                      <li>• All active jobs will be paused</li>
                      <li>• Data will be preserved but inaccessible</li>
                    </>
                  ) : (
                    <>
                      <li>• Company access will be restored</li>
                      <li>• Users can login again</li>
                      <li>• All data will become accessible</li>
                      <li>• Jobs can be resumed</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Reason Input */}
          <div>
            <label className="block text-text-primary text-sm font-medium mb-2">
              Reason for {actionText?.toLowerCase()}ing <span className="text-red-400">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e?.target?.value)}
              placeholder={`Provide a detailed reason for ${actionText?.toLowerCase()}ing this company...`}
              rows={4}
              required
              className="w-full px-4 py-3 bg-[#0f0f0f] border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 resize-none"
            />
            <p className="text-text-secondary text-xs mt-1">
              This reason will be recorded in the audit log.
            </p>
          </div>

          {/* Confirmation Checkbox */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="confirm"
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e?.target?.checked)}
              className="mt-1 w-4 h-4 rounded border-border bg-[#0f0f0f] text-orange-500 focus:ring-2 focus:ring-orange-500/50"
            />
            <label htmlFor="confirm" className="text-text-secondary text-sm">
              I understand the impact of this action and confirm that I want to {actionText?.toLowerCase()} <strong className="text-text-primary">{company?.name}</strong>
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-surface hover:bg-surface-light text-text-primary rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!reason?.trim() || !isConfirmed || isSubmitting}
              className={`flex-1 px-4 py-2.5 ${
                isActive
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-green-500 hover:bg-green-600'
              } text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? `${actionText}ing...` : `Confirm ${actionText}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuspendCompanyModal;
